import fetcher from '@/services/fetcher'
import type { InstantWinDraw, ReservationData } from '@/types'
import { toGlideUrl } from '@/utils/url-generation-utils'
import { DateTime } from 'luxon'

/**
 * Service to handle instant win reservation data storage and retrieval
 */
export const InstantWinStorage = {
  /**
   * Get the storage key for a specific instant win
   */
  getStorageKey(instantWin: InstantWinDraw | string): string {
    const code = typeof instantWin === 'string' ? instantWin : instantWin.code
    return `reserve_instant_win_${code}`
  },

  /**
   * Store reservation data in sessionStorage
   */
  storeReservation(
    instantWin: InstantWinDraw | string,
    reservationData: ReservationData
  ): void {
    sessionStorage.setItem(
      this.getStorageKey(instantWin),
      JSON.stringify(reservationData)
    )
  },

  /**
   * Remove reservation data from sessionStorage
   */
  removeReservation(instantWin: InstantWinDraw | string): void {
    sessionStorage.removeItem(this.getStorageKey(instantWin))
  },

  /**
   * Get reservation data from sessionStorage if it exists and is valid
   */
  getStoredReservation(
    instantWin: InstantWinDraw | string
  ): ReservationData | null {
    try {
      const storedData = sessionStorage.getItem(this.getStorageKey(instantWin))

      if (!storedData) {
        return null
      }

      const parsedData = JSON.parse(storedData) as ReservationData

      // Check if parsed data has required properties
      if (!parsedData.lock_key || !parsedData.expires_at) {
        this.removeReservation(instantWin)
        return null
      }

      // Handle legacy reservations without quantity
      if (typeof parsedData.quantity !== 'number') {
        this.removeReservation(instantWin)
        return null
      }

      // Check if reservation is expired
      const expiresAtDate = DateTime.fromISO(parsedData.expires_at)
      const isExpired = expiresAtDate < DateTime.now()

      if (isExpired) {
        this.removeReservation(instantWin)
        return null
      }

      return parsedData
    } catch (e) {
      // If parsing fails or data is invalid, clean up
      this.removeReservation(instantWin)
      return null
    }
  },

  /**
   * Create a new reservation for an instant win draw
   */
  async createReservation(
    instantWin: InstantWinDraw | string,
    ticketCount: number
  ): Promise<ReservationData> {
    const code = typeof instantWin === 'string' ? instantWin : instantWin.code

    const { data: response, error } = await fetcher.post(
      toGlideUrl('/instantwin/reserve'),
      {
        rowCount: ticketCount,
        code,
      }
    )

    if (error.value) {
      throw new Error(
        error.value.message || 'Failed to reserve instant win tickets'
      )
    }

    if (!response.value) {
      throw new Error('Invalid response from reservation endpoint')
    }

    const reservationData = {
      lock_key: response.value.lock_key,
      expires_at: response.value.expires_at,
      quantity: ticketCount,
      message: response.value.message,
    }

    // Validate required fields
    if (!reservationData.lock_key || !reservationData.expires_at) {
      throw new Error('Invalid reservation data returned from API')
    }

    // Store the reservation
    this.storeReservation(instantWin, reservationData)

    return reservationData
  },

  /**
   * Check for existing valid reservation or create a new one
   */
  async checkOrCreateReservation(
    instantWin: InstantWinDraw | string,
    ticketCount: number
  ): Promise<ReservationData> {
    // Try to get an existing reservation
    const existingReservation = this.getStoredReservation(instantWin)

    if (existingReservation) {
      // Check if the existing reservation is for the correct quantity
      if (existingReservation.quantity === ticketCount) {
        return existingReservation
      }

      // Quantity mismatch - remove old reservation and create new one
      this.removeReservation(instantWin)
    }

    // Create a new reservation if none exists, is invalid, or has wrong quantity
    return this.createReservation(instantWin, ticketCount)
  },
}
